import { useMemo } from "react";

import { IHeatmapColumn } from "../../../../components/HeatmapTable";

import { useAiSettings } from "../../../../../../api/hooks/defs/ai";
// hooks
import { useFeatureFlagQuery } from "../../../../../../hooks/useFeatureFlag";

import { ICohortModel } from "../../../../../../api/services/defs/cohort";

// utils
import { getColor } from "../../../../../../utils/getColor";

// config
import Config from "../../../../Config";
// schema
import { ICohortFactorSummaryPoint } from "../../../../schema";

// local schema
interface BaseProps {
	cohortMetaData: { cohorts: ICohortModel[]; [key: string]: unknown };
	factorsData: ICohortFactorSummaryPoint[];
}

/**
 * This hook is used to generate the sortable cohort data for columns in the left and right tables
 * of the "Cohort Satisfaction" heatmap within the engagement dashboard's "Cohorts" page.
 */
const useCohortTableColumns = ({ cohortMetaData, factorsData }: BaseProps): [IHeatmapColumn[], IHeatmapColumn[]] => {
	// Feature flag checks
	const { value: aiFeatureFlagEnabled } = useFeatureFlagQuery("ff-is-ai-enabled", false);

	// Company AI settings check
	const { data: aiSettings } = useAiSettings();

	// labels for the leftmost column
	const cohortColumnData: { [key: string]: string } = useMemo(() => {
		return {
			all: "All Employees",
			...(cohortMetaData?.cohorts
				.sort((coA, coB) => (coA.name.trim().toLowerCase() > coB.name.trim().toLowerCase() ? 1 : -1))
				.reduce((acc, cur) => {
					acc[cur._id.toString()] = cur.name;
					return acc;
				}, {}) || {}),
		};
	}, [cohortMetaData]);

	// function used to map empty columns for both tables
	const mapEmpty = (col: string) => [
		col,
		{
			columnId: col,
			label: col,
			data: Object.fromEntries(Object.entries(cohortColumnData).map((entry) => [entry[0], null])),
			...(col === "eNPS" ? { selectBackgroundColor: () => getColor("neutral", 2) } : {}),
		},
	];

	// empty columns for both tables
	const emptyRightTableColumns: { [key: string]: IHeatmapColumn } = Object.fromEntries(
		["Satisfaction", "eNPS"].map(mapEmpty)
	);
	const emptyFactorsTableColumns: { [key: string]: IHeatmapColumn } = Object.fromEntries(
		Config.FACTORS.FACTORS.sort().map(mapEmpty)
	);

	const rightTableColumns = useMemo(() => {
		const overallCohortMap = {};
		// actually map the data into the empty columns object if available
		const rightColumnsMap: { [key: string]: IHeatmapColumn } = factorsData
			? factorsData.reduce(
					(acc, cur) => {
						if (!cohortColumnData[cur.cohort]) {
							return acc;
						} else if (cur.factor === "eNPS" && (cur.eNPS_score || cur.eNPS_score === 0)) {
							// calculate whole number percentage of percentage with scores greater than 8 - minus whole number percentage of percentage with scores less than 7
							acc["eNPS"].data[cur.cohort] = cur.eNPS_score;
						} else {
							// to calculate average of all ratings for each cohort
							// add the current rating to the array of ratings for the cohort
							overallCohortMap[cur.cohort] = [
								...(overallCohortMap[cur.cohort] ? overallCohortMap[cur.cohort] : []),
								cur.average,
							];
							// calculate the average of all ratings for the cohort
							acc["Satisfaction"].data[cur.cohort] = parseFloat(
								overallCohortMap[cur.cohort]
									.reduce(
										(accum: number, curr: number, _: number, all: number[]) =>
											accum + curr / all.length,
										0
									)
									.toFixed(1)
							);
						}
						return acc;
					},
					{ ...emptyRightTableColumns }
				)
			: // otherwise return the empty columns
				emptyRightTableColumns;

		// Add options drawer column to the rightmost position only if feature flags are enabled
		const rightTableColumns = [...Object.values(rightColumnsMap)];

		// Check if there are at least 3 factor scores other than "Overall Satisfaction" and "eNPS"
		const hasEnoughFactorScores =
			factorsData && factorsData.filter((factorData) => factorData.factor !== "eNPS").length >= 3;

		if (aiFeatureFlagEnabled && aiSettings?.engagementCohortInsightsEnabled && hasEnoughFactorScores) {
			// Create options column data that only shows options for cohorts with enough factor scores
			const optionsColumnData: { [key: string]: string } = {};

			Object.keys(cohortColumnData).forEach((cohortId) => {
				// Check if this specific cohort has at least 3 factor scores (excluding eNPS)
				const cohortFactors =
					factorsData?.filter(
						(factorData) => factorData.cohort === cohortId && factorData.factor !== "eNPS"
					) || [];

				// Only show options if cohort has at least 3 factors with scores
				optionsColumnData[cohortId] = cohortFactors.length >= 3 ? "options" : "";
			});

			const optionsColumn: IHeatmapColumn = {
				columnId: "options",
				label: "",
				minWidth: "48px",
				data: optionsColumnData,
				selectBackgroundColor: () => "", // no color for options column
			};
			rightTableColumns.push(optionsColumn);
		}

		return rightTableColumns;
	}, [factorsData, cohortColumnData, aiFeatureFlagEnabled, aiSettings?.engagementCohortInsightsEnabled]);

	const leftTableColumns = useMemo(() => {
		// actually map the data into the empty columns object if available
		const factorsColumnsMap: { [key: string]: IHeatmapColumn } = factorsData
			? factorsData.reduce(
					(acc, cur) => {
						if (!cohortColumnData[cur.cohort] || !acc[cur.factor]) {
							return acc;
						} else if (cur.average) {
							acc[cur.factor].data[cur.cohort] = cur.average ? parseFloat(cur.average.toFixed(1)) : "";
						}
						return acc;
					},
					{ ...emptyFactorsTableColumns }
				)
			: // otherwise return the empty columns
				emptyFactorsTableColumns;

		// left table columns
		const leftTableColumns = [
			// label column
			{
				columnId: "cohorts",
				label: "Cohort",
				alignLeft: true,
				minWidth: "184px",
				maxTextLength: 176,
				selectBackgroundColor: () => "", // no color for labels
				data: { all: "All Employees", ...cohortColumnData },
			},
			// factors columns
			...Object.values(factorsColumnsMap),
		];

		// return the formatted columns for both tables
		return leftTableColumns;
	}, [cohortColumnData, factorsData]);

	return [leftTableColumns, rightTableColumns];
};

export default useCohortTableColumns;
